import { SortingState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createSelector } from "reselect";
import EditIcon from "@mui/icons-material/Edit";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";

import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import UserManagementTableContainer from "Common/Tables/UserManagementFiltersTable/UserManagementTableContainer";
import { app_modules } from "helpers/apps_modules_list";
import { showCustomSwal } from "helpers/swalHelper";
import { usePermission } from "hooks/usePermission";
import {
  fetchTeam,
  updateStatusInTeam as onUpdateStatusTeam,
  TeamExportsCSV,
} from "slices/UserManagement/team/thunk";
import Swal from "sweetalert2";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";

const TeamList = () => {
  const { id } = useParams<{ id: string }>();
  document.title = "Teams List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

    const location = useLocation();
  
    // Use the preserved table state hook
    const tableState = usePreservedTableState({
      key:  TableKeys?.USER_MANAGEMENT?.TEAM,
      defaults: {
        pageIndex: 0,
        pageSize: 10,
        search: "",
        searchInput: "",
        sorting: [],
        columnFilters: [],
        filterValue: [],
      },
    });
  
    const {
      pageIndex,
      pageSize,
      search,
      searchInput,
      sorting,
      columnFilters,
      filterValue: colValue,
      setPageIndex,
      setPageSize,
      setSearch,
      setSearchInput,
      setSorting,
      setColumnFilters,
      setFilterValue: setColValue,
      resetState,
    } = tableState;
  
    useClearStateOnNavigation(location, resetState);

  const selectorganisation = createSelector(
    (state: any) => state.teams,
    (Teams) => ({
      teams: Teams.teamList, // API's data array for current page
      rowCount: Teams.rowCount, // API's total count
      loading: Teams.loading,
      error: Teams.error,
    })
  );

  const { teams, rowCount, loading, error } = useSelector(selectorganisation);

  // Use permission hook
  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.user_management?.app_name,
    app_modules?.user_management?.modules?.team
  );

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [colValue, setColValue] = useState<any>();
  const [tableInstance, setTableInstance] = useState<any>();
  // const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Fetch data from server on any table state change
  const getBackendSortKey = (key: string) => {
    const map: Record<string, string> = {
      department_name: "department_name",
      organization_name: "organization_name",
      team_name: "team_name",
      created_at: "created_at",
      updated_at: "updated_at",
    };
    return map[key] || key;
  };

  const sortField = sorting[0]?.id ? getBackendSortKey(sorting[0].id) : "";

  useEffect(() => {
    dispatch(
      fetchTeam({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: columnFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    columnFilters,
    colValue,
    sortField,
    sortDirection,
  ]);

  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);


  // Handle status toggle
  const handleStatusToggle = async (org: any) => {
    const updatedStatus = org.is_active === true ? false : true;

    // Show loading modal
    showCustomSwal({
      title: "Updating...",
      text: "Please wait while we update the team status.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const update_payload = {
        team_name: org?.team_name,
        department: org?.department,
        team_id: org?.team_id,
        is_active: updatedStatus,
      };

      await dispatch(
        onUpdateStatusTeam({ id: org?.team_id, data: update_payload })
      ).unwrap();

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Status Updated",
        text: `Team ${
          updatedStatus ? "activated" : "deactivated"
        } successfully.`,
      });

      dispatch(
        fetchTeam({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: columnFilters,
        })
      );
    } catch (err: any) {
      console.error("Update Status Error:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to update team status";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Update Failed",
        text: errorMessage,
      });
    }
  };

  const DynamicAction = canUpdate || canDelete
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => {
            const menuItems = [
              {
                icon: EditIcon,
                label: "Edit",
                permission: canUpdate,
                onClick: (team: any) => {
                  navigate(`/team-forms/${team?.team_id || team?.id}`);
                },
                color: "primary",
              },
            ];
            return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
          },
        },
      ]
    : [];
  // Table columns
  const columns = useMemo(
    () => [
       {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Team",
        accessorKey: "team_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.USER_MANAGEMENT.TEAM.DROPDOWN(
          "team_name"
        )}`,
      },
      {
        header: "Department",
        accessorKey: "department_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.USER_MANAGEMENT.TEAM.DROPDOWN(
          "department_name"
        )}`,
        cell: ({ row }: { row: any }) => {
          return row.original.department.department_name;
        },
      },
      {
        header: "Organisations",
        accessorKey: "organization_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT.USER_MANAGEMENT.TEAM.DROPDOWN(
          "organization_name"
        )}`,
        cell: ({ row }: { row: any }) => {
          return row.original.organization.organization_name;
        },
      },

      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleStatusToggle(org)}
            >
              <i
                className={`bi ${
                  org.is_active
                    ? "bi-toggle-on text-success"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user?.username}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user?.username}
            date={row.original.updated_at}
          />
        ),
      },

      ...DynamicAction,
    ],
    [dispatch, teams, handleStatusToggle, canUpdate, canDelete]
  );

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Teams</h6>
                </Col>
                <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                  {/* Search Box */}
                  <SearchBox
                    value={searchInput}
                    onChange={(e: any) => setSearchInput(e.target.value)}
                    placeholder="Search..."
                  />

                  {/* Column Visibility + Actions */}
                  <Col xs={12} md={12} lg={8} xl={6} className="">
                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                      {/* Left side: Column Visibility */}
                      <div className="">
                        {tableInstance && (
                          <ColumnVisibilityDropdown
                            columns={tableInstance.getAllLeafColumns()}
                            excludedColumns={["action", "sr_no"]}
                          />
                        )}
                      </div>

                      <div className="hstack gap-2">
                        {canView && (
                          <ExportButton
                            onExport={async () => {
                              return await TeamExportsCSV({
                                search,
                                filters: columnFilters,
                              });
                            }}
                          />
                        )}
                      </div>
                      <div>
                        {canCreate && (
                          <ActionButton
                            label="Add Teams"
                            icon="bi bi-plus-circle"
                            navigateLink="/team-forms"
                            variant="primary"
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <UserManagementTableContainer
                  columns={columns}
                  data={teams && teams.length > 0 ? teams : []}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setColValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  manualSorting={true}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search teams..."
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default TeamList;
